import React, { useState, useMemo, useEffect } from 'react';
import { useTimeClock } from '../hooks/useTimeClock';
import { OvertimeRule, Shift, LeaveCategory, RateDefinition, DayOfWeek, ClockOutRules, LeaveCategoryCalculation, Employee, NameDisplayFormat } from '../../shared/types.js';
import Button from './Button';
import { cloneDeep, formatEmployeeName, createDefaultDailyConditions, createEmptyRateDefinition, generateUUID } from '../utils.js';
import NameEditModal from './NameEditModal';
import AssignEmployeesModal from './AssignEmployeesModal';
import { EditIcon, TrashIcon } from './icons';

const NavItem: React.FC<{ label: string; active: boolean; onClick: () => void }> = ({ label, active, onClick }) => (
    <button
        onClick={onClick}
        className={`w-full text-left p-3 rounded-lg transition-colors text-sm ${active ? 'bg-brand-secondary text-white font-bold' : 'hover:bg-gray-200 dark:hover:bg-gray-700'}`}
    >
        {label}
    </button>
);

const NavHeader: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <div className="px-3 pt-4 pb-1 text-xs font-bold uppercase text-gray-500 dark:text-gray-400 tracking-wider">
        {children}
    </div>
);

const RateRuleEditor: React.FC<{
    rateNumber: 1 | 2;
    rateDef: RateDefinition;
    onRateChange: (field: keyof RateDefinition | `daily.${DayOfWeek}`, value: any) => void;
}> = ({ rateNumber, rateDef, onRateChange }) => {
    const handleCheckboxChange = (key: keyof RateDefinition | `daily.${DayOfWeek}`, checked: boolean) => {
        if (key.startsWith('daily.')) {
            const day = key.split('.')[1] as DayOfWeek;
            onRateChange('daily', { ...rateDef.daily, [day]: { ...rateDef.daily[day], enabled: checked } });
        } else if (key === 'weekly') {
            onRateChange('weekly', { ...rateDef.weekly, enabled: checked });
        }
    };
    const handleInputChange = (key: 'rate' | 'weeklyThreshold' | `daily.${DayOfWeek}`, value: string) => {
        if (key === 'rate') {
             onRateChange('rate', parseFloat(value) || 0);
        } else if (key === 'weeklyThreshold') {
            onRateChange('weekly', { ...rateDef.weekly, threshold: parseInt(value, 10) || 0 });
        } else if (key.startsWith('daily.')) {
            const day = key.split('.')[1] as DayOfWeek;
            onRateChange('daily', { ...rateDef.daily, [day]: { ...rateDef.daily[day], threshold: parseFloat(value) || 0 } });
        }
    };
    const numberInputClasses = "w-24 text-center bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-700 rounded-md py-1 px-2 text-gray-900 dark:text-white focus:outline-none focus:ring-1 focus:ring-brand-secondary focus:border-brand-secondary disabled:opacity-50";
    const checkboxClasses = "h-4 w-4 rounded text-brand-secondary focus:ring-brand-secondary bg-gray-200 dark:bg-gray-600 border-gray-300 dark:border-gray-500";
    return (
        <div className="border border-gray-300 dark:border-gray-700 p-4 rounded-lg space-y-4 bg-gray-50 dark:bg-gray-900/50">
            <div className="flex items-center gap-3">
                <label htmlFor={`rate-${rateNumber}`} className="font-semibold text-xl text-gray-900 dark:text-white">Rate {rateNumber}</label>
                <input id={`rate-${rateNumber}`} type="number" value={rateDef.rate} onChange={(e) => handleInputChange('rate', e.target.value)} step="0.1" className={numberInputClasses} />
                <span className="text-gray-500 dark:text-gray-400">times base pay</span>
            </div>
            <div className="grid grid-cols-2 gap-x-4 border-b border-gray-200 dark:border-gray-600 pb-3 mb-3">
                <h3 className="font-semibold text-xl text-gray-900 dark:text-white"></h3>
                <h3 className="font-semibold text-xl text-gray-900 dark:text-white">After Hour</h3>
            </div>
            <div className="grid grid-cols-2 gap-x-4 items-center">
                <div className="flex items-center gap-3">
                    <input id={`weekly-enabled-${rateNumber}`} type="checkbox" checked={rateDef.weekly.enabled} onChange={(e) => handleCheckboxChange('weekly', e.target.checked)} className={checkboxClasses}/>
                    <label htmlFor={`weekly-enabled-${rateNumber}`} className="text-gray-700 dark:text-gray-300 cursor-pointer">Weekly</label>
                </div>
                <div>
                    <input type="number" value={rateDef.weekly.threshold} onChange={(e) => handleInputChange('weeklyThreshold', e.target.value)} className={numberInputClasses} disabled={!rateDef.weekly.enabled}/>
                </div>
            </div>
            <div>
                <p className="text-gray-500 dark:text-gray-400 underline pt-2 mb-3">Daily</p>
                <div className="space-y-2">
                 {Object.values(DayOfWeek).map(day => (
                     <div key={day} className="grid grid-cols-2 gap-x-4 items-center">
                         <div className="flex items-center gap-3">
                            <input id={`daily-enabled-${day}-${rateNumber}`} type="checkbox" checked={rateDef.daily[day]?.enabled ?? false} onChange={(e) => handleCheckboxChange(`daily.${day}`, e.target.checked)} className={checkboxClasses} />
                            <label htmlFor={`daily-enabled-${day}-${rateNumber}`} className="w-24 text-gray-700 dark:text-gray-300 cursor-pointer">{day}</label>
                         </div>
                         <div>
                            <input type="number" value={rateDef.daily[day]?.threshold ?? 8} onChange={(e) => handleInputChange(`daily.${day}`, e.target.value)} className={numberInputClasses} disabled={!rateDef.daily[day]?.enabled} />
                         </div>
                     </div>
                 ))}
                </div>
            </div>
        </div>
    );
};

const ClockOutRulesForm: React.FC<{ rules: ClockOutRules; onChange: (newRules: ClockOutRules) => void; }> = ({ rules, onChange }) => {
    const handleRuleChange = <K extends keyof ClockOutRules>(key: K, value: ClockOutRules[K]) => {
        onChange({ ...rules, [key]: value });
    };
    const handleNestedChange = (key: 'autoClockOut', subkey: 'enabled' | 'minutes', value: boolean | number) => {
        handleRuleChange(key, { ...rules[key], [subkey]: value as any });
    };
    const inputClass = "w-20 bg-white dark:bg-gray-700 p-1 rounded border border-gray-300 dark:border-gray-600 focus:ring-brand-secondary focus:border-brand-secondary disabled:opacity-50 text-center";
    const checkboxClass = "w-5 h-5 bg-gray-200 dark:bg-gray-600 border-gray-300 dark:border-gray-500 rounded text-brand-secondary focus:ring-brand-secondary";
    return (
        <div className="space-y-4 text-gray-700 dark:text-gray-300">
            <label className="flex items-center gap-3">
                <input 
                    type="checkbox" 
                    className={checkboxClass} 
                    checked={rules.autoClockOut.enabled}
                    onChange={e => handleNestedChange('autoClockOut', 'enabled', e.target.checked)}
                />
                <span>Auto clock out</span>
                <input 
                    type="number" 
                    className={inputClass} 
                    value={rules.autoClockOut.minutes}
                    onChange={e => handleNestedChange('autoClockOut', 'minutes', parseInt(e.target.value, 10) || 0)}
                    disabled={!rules.autoClockOut.enabled}
                />
                <span>minutes after shift ends</span>
            </label>
        </div>
    );
};

const sanitizeOvertimeRule = (rule: OvertimeRule | undefined): OvertimeRule | null => {
    if (!rule) return null;
    const item = cloneDeep(rule);
    const sanitizedRates = (item.rates || []).map(rate => ({
        ...createEmptyRateDefinition(),
        ...rate,
        daily: {
            ...createDefaultDailyConditions(),
            ...(rate.daily || {})
        }
    }));
    while (sanitizedRates.length < 2) {
        sanitizedRates.push(createEmptyRateDefinition());
    }
    item.rates = sanitizedRates.slice(0, 2) as [RateDefinition, RateDefinition];
    return item;
};

const sanitizeShift = (shift: Shift | undefined): Shift | null => {
    if (!shift) return null;
    const item = cloneDeep(shift);
    if (!item.clockOutRules) {
        item.clockOutRules = {
            autoClockOut: { enabled: false, minutes: 15 }
        };
    }
    return item;
};

const ClassificationsTab: React.FC = () => {
    const { 
        overtimeRules, addOvertimeRule, updateOvertimeRule, deleteOvertimeRule, setOvertimeRuleAssignments,
        shifts, addShift, updateShift, deleteShift,
        leaveCategories, updateLeaveClassifications, addCustomLeaveCategory, updateCustomLeaveCategory, deleteCustomLeaveCategory,
        employees,
        displayGroups
    } = useTimeClock();

    const [activeView, setActiveView] = useState<{ type: 'overtime' | 'shift' | 'leave'; id: string } | null>(null);
    const [editingItem, setEditingItem] = useState<OvertimeRule | Shift | null>(null);
    const [localLeaveClassifications, setLocalLeaveClassifications] = useState<LeaveCategory[]>([]);
    const [initialLeaveClassifications, setInitialLeaveClassifications] = useState<LeaveCategory[]>([]);
    const employeesInAnyGroup = useMemo(() => {
        const groupEmployeeIds = new Set(displayGroups.flatMap(g => g.employeeIds));
        return employees.filter(e => groupEmployeeIds.has(e.id));
    }, [employees, displayGroups]);
    const [isNameModalOpen, setIsNameModalOpen] = useState(false);
    const [isCustomLeaveModalOpen, setIsCustomLeaveModalOpen] = useState(false);
    const [editingCustomLeave, setEditingCustomLeave] = useState<Partial<LeaveCategory> | null>(null);
    const [isAssignModalOpen, setIsAssignModalOpen] = useState(false);
    const sortedOvertimeRules = useMemo(() => [...overtimeRules].sort((a, b) => a.name.localeCompare(b.name)), [overtimeRules]);
    const sortedShifts = useMemo(() => [...shifts].sort((a, b) => a.name.localeCompare(b.name)), [shifts]);
    const sortedLeaveCategories = useMemo(() => [...leaveCategories].sort((a,b) => a.name.localeCompare(b.name)), [leaveCategories]);
    useEffect(() => {
        if (activeView) {
            let item: OvertimeRule | Shift | null = null;
            if (activeView.type === 'overtime') {
                const foundRule = overtimeRules.find(r => r.id === activeView.id);
                item = sanitizeOvertimeRule(foundRule);
            }
            else if (activeView.type === 'shift') {
                const foundShift = shifts.find(s => s.id === activeView.id);
                item = sanitizeShift(foundShift);
            }
            setEditingItem(item);
            if (activeView.type === 'leave') {
                const initialData = cloneDeep(sortedLeaveCategories);
                setLocalLeaveClassifications(initialData);
                setInitialLeaveClassifications(initialData);
            }
        } else {
            setEditingItem(null);
        }
    }, [activeView]);
    useEffect(() => {
        if (activeView?.type === 'overtime' && editingItem && activeView.id !== 'new') {
            const currentGlobalRule = overtimeRules.find(r => r.id === activeView.id);
            const editingRule = editingItem as OvertimeRule;
            if (currentGlobalRule && JSON.stringify(currentGlobalRule.assignedEmployeeIds) !== JSON.stringify(editingRule.assignedEmployeeIds)) {
                setEditingItem(prev => ({
                    ...(prev as OvertimeRule),
                    assignedEmployeeIds: cloneDeep(currentGlobalRule.assignedEmployeeIds),
                }));
            }
        }
    }, [overtimeRules, activeView, editingItem]);
    const handleSelect = (type: 'overtime' | 'shift' | 'leave', id: string) => {
        setActiveView({ type, id });
    };
    const handleAddItem = (type: 'overtime' | 'shift') => {
        setActiveView({ type, id: 'new' });
        setIsNameModalOpen(true);
    };
    const handleSaveName = (data: { name: string }) => {
        if (activeView?.type === 'shift') addShift(data.name);
        else if (activeView?.type === 'overtime') {
            const newRule: Omit<OvertimeRule, 'id' | 'assignedEmployeeIds'> = {
                name: data.name,
                rates: [
                    { ...createEmptyRateDefinition(), rate: 1.5, weekly: { enabled: true, threshold: 40 } },
                    createEmptyRateDefinition()
                ]
            };
            addOvertimeRule(newRule);
        }
        setIsNameModalOpen(false);
        setActiveView(null);
    };
    const handleApply = async () => {
        if (!activeView) return;
        try {
            if (activeView.type === 'overtime' && editingItem) {
                await updateOvertimeRule(editingItem as OvertimeRule);
            } else if (activeView.type === 'shift' && editingItem) {
                await updateShift(editingItem as Shift);
            } else if (activeView.type === 'leave') {
                const ohrimClassifications = localLeaveClassifications
                   .filter(c => c.source === 'orangehrm')
                   .map(c => ({ id: c.id, calculation: c.calculation }));
                await updateLeaveClassifications(ohrimClassifications);
                const initialCustom = initialLeaveClassifications.filter(c => c.source === 'kiosk');
                const localCustom = localLeaveClassifications.filter(c => c.source === 'kiosk');
                const initialIds = new Set(initialCustom.map(c => c.id));
                for (const initialCat of initialCustom) {
                    if (!localCustom.some(c => c.id === initialCat.id)) {
                        await deleteCustomLeaveCategory(initialCat.id);
                    }
                }
                for (const localCat of localCustom) {
                    if (localCat.id.startsWith('new_')) {
                        await addCustomLeaveCategory(localCat.name, localCat.calculation);
                    } else if (initialIds.has(localCat.id)) {
                        const initialCat = initialCustom.find(c => c.id === localCat.id);
                        if (JSON.stringify(initialCat) !== JSON.stringify(localCat)) {
                            await updateCustomLeaveCategory(localCat);
                        }
                    }
                }
            }
            setActiveView(null);
        } catch(e: any) {
             alert(`Failed to apply changes: ${e.message}`);
        }
    };
    const handleRevert = () => {
         if (activeView) {
            if (activeView.type === 'leave') {
                setLocalLeaveClassifications(cloneDeep(initialLeaveClassifications));
            } else {
                let item: OvertimeRule | Shift | null = null;
                if (activeView.type === 'overtime') {
                    const foundRule = overtimeRules.find(r => r.id === activeView.id);
                    item = sanitizeOvertimeRule(foundRule);
                } else if (activeView.type === 'shift') {
                    const foundShift = shifts.find(s => s.id === activeView.id);
                    item = sanitizeShift(foundShift);
                }
                setEditingItem(item);
            }
        }
    };
    const handleDelete = () => {
        if (!activeView || activeView.type === 'leave') return;
        const confirmText = `Are you sure you want to delete this item?`;
        if (confirm(confirmText)) {
            if (activeView.type === 'overtime') deleteOvertimeRule(activeView.id);
            else if (activeView.type === 'shift') deleteShift(activeView.id);
            setActiveView(null);
        }
    };
    const isDirty = useMemo(() => {
        if (!activeView) return false;
        if (activeView.type === 'leave') {
            return JSON.stringify(initialLeaveClassifications) !== JSON.stringify(localLeaveClassifications);
        }
        let originalItem;
        if (activeView.type === 'overtime') originalItem = sanitizeOvertimeRule(overtimeRules.find(r => r.id === activeView.id));
        else if (activeView.type === 'shift') originalItem = sanitizeShift(shifts.find(s => s.id === activeView.id));
        return JSON.stringify(originalItem) !== JSON.stringify(editingItem);
    }, [editingItem, activeView, overtimeRules, shifts, localLeaveClassifications, initialLeaveClassifications]);
    const renderOvertimeEditor = () => {
        const rule = editingItem as OvertimeRule;
        if (!rule || !rule.rates) return null;
        const assignedEmployees = employees.filter(e => (rule.assignedEmployeeIds || []).includes(e.id))
            .sort((a,b) => (a.lastName || '').localeCompare(b.lastName || '') || (a.firstName || '').localeCompare(b.firstName || ''));
        const handleRateChange = (rateIndex: 0 | 1, field: any, value: any) => {
            const newRates = [...rule.rates] as [RateDefinition, RateDefinition];
            const targetRate = { ...newRates[rateIndex] };
            if (field === 'rate') targetRate.rate = value;
            else if (field === 'weekly') targetRate.weekly = value;
            else if (field === 'daily') targetRate.daily = value;
            newRates[rateIndex] = targetRate;
            setEditingItem({ ...rule, rates: newRates });
        };
        const handleSaveAssignments = (assignedEmployeeIds: string[]) => {
            setOvertimeRuleAssignments(rule.id, assignedEmployeeIds);
            setIsAssignModalOpen(false);
        };
        return (
            <div className="space-y-6">
                <input type="text" value={rule.name} onChange={e => setEditingItem({...rule, name: e.target.value})} className="text-2xl font-bold bg-transparent focus:bg-white dark:focus:bg-gray-700 p-2 rounded-md w-full focus:outline-none focus:ring-2 focus:ring-brand-secondary"/>
                <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
                    <RateRuleEditor rateNumber={1} rateDef={rule.rates[0]} onRateChange={(field, value) => handleRateChange(0, field, value)} />
                    <RateRuleEditor rateNumber={2} rateDef={rule.rates[1]} onRateChange={(field, value) => handleRateChange(1, field, value)} />
                </div>
                 <div className="border border-gray-300 dark:border-gray-700 rounded-lg flex flex-col h-64">
                    <h3 className="p-2 text-center font-semibold bg-gray-100 dark:bg-gray-700/50">Assigned Employees</h3>
                    <div className="flex-grow overflow-y-auto p-2">
                        {assignedEmployees.map(emp => <div key={emp.id} className="p-1">{formatEmployeeName(emp, NameDisplayFormat.LAST_FIRST)}</div>)}
                        {assignedEmployees.length === 0 && <p className="text-gray-500 text-center p-4">None</p>}
                    </div>
                    <div className="p-2 border-t border-gray-200 dark:border-gray-700 text-right">
                        <Button variant="secondary" size="sm" onClick={() => setIsAssignModalOpen(true)}>Edit Assignments</Button>
                    </div>
                </div>
                {isAssignModalOpen && (
                    <AssignEmployeesModal isOpen={isAssignModalOpen} onClose={() => setIsAssignModalOpen(false)} onSave={handleSaveAssignments} rule={rule} employees={employeesInAnyGroup} />
                )}
            </div>
        );
    };
    const renderShiftEditor = () => {
        const shift = editingItem as Shift;
        if (!shift) return null;
        const clockOutRules = shift.clockOutRules || {
            autoClockOut: { enabled: false, minutes: 15 }
        };
        const FormRow: React.FC<{label: string, children: React.ReactNode}> = ({ label, children }) => (
            <div className="grid grid-cols-3 items-center gap-4">
                <label className="text-right text-gray-600 dark:text-gray-300 font-medium">{label}:</label>
                <div className="col-span-2">{children}</div>
            </div>
        );
        const inputClass = "block w-full bg-gray-50 dark:bg-gray-700 border-gray-300 dark:border-gray-600 rounded-md shadow-sm py-2 px-3 text-gray-900 dark:text-white focus:outline-none focus:ring-brand-secondary focus:border-brand-secondary";
        return (
             <div className="space-y-6">
                <input type="text" value={shift.name} onChange={e => setEditingItem({...shift, name: e.target.value})} className="text-2xl font-bold bg-transparent focus:bg-white dark:focus:bg-gray-700 p-2 rounded-md w-full focus:outline-none focus:ring-2 focus:ring-brand-secondary"/>
                <div className="space-y-3 max-w-md">
                   <FormRow label="Shift Start">
                       <input type="time" value={shift.startTime} onChange={e => setEditingItem({...shift, startTime: e.target.value})} className={inputClass} />
                   </FormRow>
                   <FormRow label="Stop Time">
                       <input type="time" value={shift.stopTime} onChange={e => setEditingItem({...shift, stopTime: e.target.value})} className={inputClass} />
                   </FormRow>
                </div>
                <div className="pt-4">
                    <div className="bg-gray-100 dark:bg-gray-700 p-4 rounded-md">
                        <ClockOutRulesForm rules={clockOutRules} onChange={rules => setEditingItem({...shift, clockOutRules: rules})} />
                    </div>
                </div>
            </div>
        );
    };
    const renderLeaveEditor = () => {
        const handleClassificationChange = (id: string, newCalculation: LeaveCategoryCalculation) => {
            setLocalLeaveClassifications(prev => prev.map(cat => 
                cat.id === id ? { ...cat, calculation: newCalculation } : cat
            ));
        };
        const orangeHRMLeaves = localLeaveClassifications.filter(c => c.source === 'orangehrm');
        const customLeaves = localLeaveClassifications.filter(c => c.source === 'kiosk');
        const handleAddCustom = () => {
            setEditingCustomLeave(null);
            setIsCustomLeaveModalOpen(true);
        };
        const handleEditCustom = (category: LeaveCategory) => {
            setEditingCustomLeave(category);
            setIsCustomLeaveModalOpen(true);
        };
        const handleDeleteCustom = (id: string) => {
            if (confirm("Are you sure you want to delete this custom leave type? This change will be saved when you click Apply.")) {
                setLocalLeaveClassifications(prev => prev.filter(cat => cat.id !== id));
            }
        };
        const handleSaveCustomLeave = (data: { name: string; calculation?: LeaveCategoryCalculation }) => {
            if (editingCustomLeave?.id) {
                setLocalLeaveClassifications(prev => prev.map(cat => 
                    cat.id === editingCustomLeave.id 
                    ? { ...cat, name: data.name, calculation: data.calculation || cat.calculation } 
                    : cat
                ));
            } else {
                const newCategory: LeaveCategory = {
                    id: `new_${generateUUID()}`,
                    name: data.name,
                    calculation: data.calculation!,
                    source: 'kiosk'
                };
                setLocalLeaveClassifications(prev => [...prev, newCategory]);
            }
            setIsCustomLeaveModalOpen(false);
        };
        return (
            <div className="space-y-8">
                <div>
                    <h3 className="text-xl font-bold text-gray-700 dark:text-gray-200">OrangeHRM Leave Types</h3>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 mb-4">Classify leave from OrangeHRM as Paid or Unpaid for reporting. Leave types themselves are managed within OrangeHRM.</p>
                    <div className="overflow-x-auto border border-gray-200 dark:border-gray-700 rounded-lg">
                        <table className="w-full text-left">
                            <thead className="bg-gray-50 dark:bg-gray-700/50">
                                <tr>
                                    <th className="p-3 font-semibold">Leave Type Name</th>
                                    <th className="p-3 font-semibold">Classification</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                                {orangeHRMLeaves.map(category => (
                                    <tr key={category.id}>
                                        <td className="p-3">{category.name}</td>
                                        <td className="p-3">
                                            <select
                                                value={category.calculation}
                                                onChange={(e) => handleClassificationChange(category.id, e.target.value as LeaveCategoryCalculation)}
                                                className="block w-48 bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-brand-secondary focus:border-brand-secondary"
                                            >
                                                <option value={LeaveCategoryCalculation.PAID}>Paid</option>
                                                <option value={LeaveCategoryCalculation.UNPAID}>Unpaid</option>
                                            </select>
                                        </td>
                                    </tr>
                                ))}
                                 {orangeHRMLeaves.length === 0 && (
                                    <tr><td colSpan={2} className="p-4 text-center text-gray-500">No leave types found in OrangeHRM database.</td></tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
                <div>
                    <h3 className="text-xl font-bold text-gray-700 dark:text-gray-200">Custom Leave Types</h3>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 mb-4">Add custom leave types (e.g., Stat Holiday) managed only by the kiosk.</p>
                     <div className="overflow-x-auto border border-gray-200 dark:border-gray-700 rounded-lg">
                        <table className="w-full text-left">
                            <thead className="bg-gray-50 dark:bg-gray-700/50">
                                <tr>
                                    <th className="p-3 font-semibold">Leave Type Name</th>
                                    <th className="p-3 font-semibold">Classification</th>
                                    <th className="p-3 font-semibold text-right">Actions</th>
                                </tr>
                            </thead>
                             <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                                {customLeaves.map(category => (
                                    <tr key={category.id}>
                                        <td className="p-3">{category.name}</td>
                                        <td className="p-3">{category.calculation}</td>
                                        <td className="p-3 text-right">
                                            <div className="flex justify-end gap-2">
                                                <Button variant="ghost" size="sm" onClick={() => handleEditCustom(category)}><EditIcon className="w-5 h-5" /></Button>
                                                <Button variant="ghost" size="sm" onClick={() => handleDeleteCustom(category.id)}><TrashIcon className="w-5 h-5 text-red-500" /></Button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                                {customLeaves.length === 0 && (
                                    <tr><td colSpan={3} className="p-4 text-center text-gray-500">No custom leave types defined.</td></tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                    <div className="mt-4">
                        <Button onClick={handleAddCustom}>+ Add Custom Leave</Button>
                    </div>
                </div>
                <NameEditModal
                    isOpen={isCustomLeaveModalOpen}
                    onClose={() => setIsCustomLeaveModalOpen(false)}
                    onSave={handleSaveCustomLeave}
                    title={editingCustomLeave?.id ? 'Edit Custom Leave' : 'Add Custom Leave'}
                    initialName={editingCustomLeave?.name}
                    initialCalculation={editingCustomLeave?.calculation}
                    showCalculation={true}
                />
            </div>
        );
    };
    return (
        <div className="flex bg-white dark:bg-gray-800 text-gray-900 dark:text-white rounded-lg shadow-lg">
            <div className="w-1/3 md:w-1/4 flex flex-col border-r border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50">
                <div className="flex-grow p-2 space-y-1 overflow-y-auto">
                    <NavHeader>Overtime Rules</NavHeader>
                    {sortedOvertimeRules.map(r => <NavItem key={r.id} label={r.name} active={activeView?.type === 'overtime' && activeView.id === r.id} onClick={() => handleSelect('overtime', r.id)} />)}
                    <Button size="sm" variant="ghost" className="w-full mt-1" onClick={() => handleAddItem('overtime')}>+ Add Rule</Button>
                    <NavHeader>Shifts</NavHeader>
                    {sortedShifts.map(s => <NavItem key={s.id} label={s.name} active={activeView?.type === 'shift' && activeView.id === s.id} onClick={() => handleSelect('shift', s.id)} />)}
                     <Button size="sm" variant="ghost" className="w-full mt-1" onClick={() => handleAddItem('shift')}>+ Add Shift</Button>
                    <NavHeader>Leave</NavHeader>
                    <NavItem label="Leave Categories" active={activeView?.type === 'leave'} onClick={() => handleSelect('leave', 'all')} />
                </div>
            </div>
            <div className="w-2/3 md:w-3/4 flex flex-col">
                {activeView ? (
                    <>
                        <div className="flex-grow p-6 overflow-y-auto">
                           {activeView.type === 'overtime' && renderOvertimeEditor()}
                           {activeView.type === 'shift' && renderShiftEditor()}
                           {activeView.type === 'leave' && renderLeaveEditor()}
                        </div>
                        <div className="p-4 border-t border-gray-200 dark:border-gray-700 bg-gray-100 dark:bg-gray-900/40 flex justify-between items-center">
                            <Button variant="danger" onClick={handleDelete} disabled={!activeView || activeView.type === 'leave' || (activeView.type === 'overtime' && overtimeRules.length <= 1) || (activeView.type === 'shift' && shifts.length <= 1)}>Delete</Button>
                            <div className="flex gap-4">
                                <Button variant="secondary" onClick={handleRevert} disabled={!isDirty}>Revert</Button>
                                <Button onClick={handleApply} disabled={!isDirty}>Apply</Button>
                            </div>
                        </div>
                    </>
                ) : (
                    <div className="flex items-center justify-center h-full text-gray-500">Select an item to configure it.</div>
                )}
            </div>
            <NameEditModal 
                isOpen={isNameModalOpen}
                onClose={() => setIsNameModalOpen(false)}
                onSave={handleSaveName}
                title={`Add New ${activeView?.type === 'shift' ? 'Shift' : 'Overtime Rule'}`}
            />
        </div>
    );
};

export default ClassificationsTab;
